import React, { useState, useRef, useCallback, useEffect } from "react";
import { FaMicrophone, FaPause, FaStop } from "react-icons/fa";

function getSupportedMimeType() {
  const types = [
    "audio/ogg;codecs=opus",
    "audio/webm;codecs=opus",
    "audio/mp4",
    "audio/aac",
  ];
  for (const t of types) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

function getExtension(mime) {
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4") || mime.includes("aac")) return "m4a";
  return "webm";
}

function extractPeaks(blob, numPeaks = 80) {
  return new Promise((resolve) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    blob.arrayBuffer().then((buf) => {
      ctx.decodeAudioData(buf).then((audioBuffer) => {
        const data = audioBuffer.getChannelData(0);
        const step = Math.floor(data.length / numPeaks);
        const peaks = [];
        for (let i = 0; i < numPeaks; i++) {
          let sum = 0;
          const start = i * step;
          for (let j = 0; j < step; j++) {
            sum += Math.abs(data[start + j] || 0);
          }
          peaks.push(sum / step);
        }
        const max = Math.max(...peaks, 0.001);
        ctx.close();
        resolve(peaks.map((p) => Math.min(p / max, 1)));
      }).catch(() => { ctx.close(); resolve(Array(numPeaks).fill(0.1)); });
    }).catch(() => { ctx.close(); resolve(Array(numPeaks).fill(0.1)); });
  });
}

export default function AudioRecorder({ onRecordingComplete, autoStart }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const animFrameRef = useRef(null);
  const startTimeRef = useRef(0);
  const accumulatedRef = useRef(0);
  const startedRef = useRef(false);

  const mimeType = useRef(getSupportedMimeType());
  const extension = useRef(getExtension(mimeType.current));

  const drawLiveWaveform = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx2d = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const render = () => {
      if ((!isRecording && !mediaRecorderRef.current) || isPaused) return;
      analyser.getByteFrequencyData(dataArray);
      const w = canvas.width;
      const h = canvas.height;
      ctx2d.clearRect(0, 0, w, h);

      const barCount = 40;
      const barWidth = Math.max((w / barCount) - 1, 2);
      const gap = 1;

      for (let i = 0; i < barCount; i++) {
        const idx = Math.floor((i / barCount) * bufferLength);
        const val = dataArray[idx] / 255;
        const barH = Math.max(val * h * 0.9, 2);
        const x = i * (barWidth + gap);
        const y = (h - barH) / 2;

        ctx2d.fillStyle = "#00a884";
        ctx2d.beginPath();
        if (ctx2d.roundRect) {
          ctx2d.roundRect(x, y, barWidth, barH, 1);
        } else {
          ctx2d.rect(x, y, barWidth, barH);
        }
        ctx2d.fill();
      }

      animFrameRef.current = requestAnimationFrame(render);
    };
    render();
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (isRecording && !isPaused) {
      drawLiveWaveform();
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isRecording, isPaused, drawLiveWaveform]);

  const cleanup = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} }
    clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    mediaRecorderRef.current = null;
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const opts = mimeType.current ? { mimeType: mimeType.current } : {};
      const recorder = new MediaRecorder(stream, opts);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType.current });
        const elapsed = (Date.now() - startTimeRef.current) / 1000 + accumulatedRef.current;
        cleanup();
        setIsRecording(false);
        setIsPaused(false);

        if (elapsed < 0.5) {
          setDuration(0);
          accumulatedRef.current = 0;
          startedRef.current = false;
          return;
        }

        const peaks = await extractPeaks(blob);
        startedRef.current = false;
        onRecordingComplete(blob, elapsed, peaks, extension.current);
      };

      recorder.start(100);
      startTimeRef.current = Date.now();
      accumulatedRef.current = 0;
      setIsRecording(true);
      setIsPaused(false);
      setDuration(0);

      timerRef.current = setInterval(() => {
        const ms = Date.now() - startTimeRef.current + accumulatedRef.current * 1000;
        setDuration(ms / 1000);
      }, 100);
    } catch (err) {
      console.error("Erreur micro:", err);
      startedRef.current = false;
      alert("Impossible d'acceder au microphone.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      accumulatedRef.current += (Date.now() - startTimeRef.current) / 1000;
      clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      startTimeRef.current = Date.now();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        const ms = Date.now() - startTimeRef.current + accumulatedRef.current * 1000;
        setDuration(ms / 1000);
      }, 100);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  useEffect(() => {
    if (autoStart) {
      startRecording();
    }
    return () => { cleanup(); startedRef.current = false; };
  }, []);

  const fmt = (sec) => {
    const s = Math.floor(sec || 0);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m + ":" + r.toString().padStart(2, "0");
  };

  if (!isRecording) {
    return (
      <div className="recorder-idle">
        <button className="mic-btn" onClick={startRecording} title="Enregistrer un message vocal">
          <FaMicrophone size={20} />
        </button>
        <span className="recorder-idle-hint">
          Appuyez pour enregistrer
        </span>
      </div>
    );
  }

  return (
    <div className="recorder-live">
      <div className="recorder-live-top">
        <button
          className="recorder-pause-btn"
          onClick={isPaused ? resumeRecording : pauseRecording}
          title={isPaused ? "Reprendre" : "Mettre en pause"}
        >
          {isPaused ? <FaMicrophone size={14} /> : <FaPause size={14} />}
        </button>
        <span className={`recorder-duration ${isPaused ? "paused" : ""}`}>
          {fmt(duration)}
        </span>
        <canvas ref={canvasRef} width={300} height={32} style={{ flex: 1, height: 32 }} />
        <button className="recorder-stop-btn" onClick={stopRecording} title="Arreter l'enregistrement">
          <FaStop size={14} color="#fff" />
        </button>
      </div>
    </div>
  );
}

export { extractPeaks, getExtension };
